/* ==========================================================================
   Brig Stamp — comportamento global
   Scroll suave, cabeçalho, menu, revelações, títulos, cartões com
   inclinação, faixas em movimento e o carrinho (gaveta + AJAX).
   As secções com lógica própria são elementos personalizados (<bs-…>)
   definidos nos seus ficheiros.
   ========================================================================== */
(() => {
  const BS = (window.BS = window.BS || {});
  BS.reduzido = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  BS.toque = window.matchMedia('(hover: none)').matches;
  BS.velocidade = 0;

  if (window.gsap) {
    const plugins = [window.ScrollTrigger, window.SplitText, window.CustomEase].filter(Boolean);
    gsap.registerPlugin(...plugins);
    // A curva do scroll horizontal da Outder (ver SCROLLHORIZONTAL.md).
    if (window.CustomEase) CustomEase.create('horScroll', '0.25,0,0.75,1');
  }

  // ——— Dinheiro ———
  BS.dinheiro = (centimos, formato = BS.moeda || '{{amount_with_comma_separator}} €') => {
    const n = Number(centimos) / 100;
    const fmt = (num, casas, milhar, decimal) => {
      const [i, d] = num.toFixed(casas).split('.');
      const inteiro = i.replace(/\B(?=(\d{3})+(?!\d))/g, milhar);
      return d ? `${inteiro}${decimal}${d}` : inteiro;
    };
    const tipo = (formato.match(/\{\{\s*(\w+)\s*\}\}/) || [])[1] || 'amount';
    const tabela = {
      amount: [2, ',', '.'],
      amount_no_decimals: [0, ',', '.'],
      amount_with_comma_separator: [2, '.', ','],
      amount_no_decimals_with_comma_separator: [0, '.', ','],
      amount_with_apostrophe_separator: [2, "'", '.'],
      amount_with_space_separator: [2, ' ', ','],
      amount_with_period_and_space_separator: [2, ' ', '.'],
    };
    const [casas, milhar, decimal] = tabela[tipo] || tabela.amount;
    return formato.replace(/\{\{\s*\w+\s*\}\}/, fmt(n, casas, milhar, decimal)).replace(/<[^>]*>/g, '');
  };

  BS.carregar3D = () => (BS._modulo3d ||= import(BS.ativos.modelo3d));

  // ——— Scroll suave (Lenis) ———
  function iniciarScroll() {
    let ultimoY = window.scrollY;
    let ultimoT = performance.now();
    const medir = () => {
      const agora = performance.now();
      const dy = window.scrollY - ultimoY;
      BS.velocidade = (dy / Math.max(agora - ultimoT, 1)) * 1000;
      ultimoY = window.scrollY;
      ultimoT = agora;
    };
    const suave = window.Lenis && !BS.reduzido && !BS.editor && BS.definicoes?.suave && !BS.toque;
    if (!suave) {
      window.addEventListener('scroll', medir, { passive: true });
      return;
    }
    const lenis = new Lenis({ lerp: 0.095, smoothWheel: true, wheelMultiplier: 1 });
    BS.lenis = lenis;
    lenis.on('scroll', (e) => {
      BS.velocidade = e.velocity * 60;
      if (window.ScrollTrigger) ScrollTrigger.update();
    });
    if (window.gsap) {
      gsap.ticker.add((t) => lenis.raf(t * 1000));
      gsap.ticker.lagSmoothing(0);
    } else {
      const raf = (t) => {
        lenis.raf(t);
        requestAnimationFrame(raf);
      };
      requestAnimationFrame(raf);
    }
    document.addEventListener('click', (e) => {
      const a = e.target.closest('a[href^="#"]');
      const id = a?.getAttribute('href');
      if (!id || id.length < 2) return;
      const alvo = document.querySelector(id);
      if (!alvo) return;
      e.preventDefault();
      lenis.scrollTo(alvo, { offset: -90 });
    });
  }

  // ——— Cabeçalho e menu ———
  function iniciarCabecalho() {
    const cab = document.querySelector('[data-bs-cabecalho]');
    if (!cab) return;
    let ultimo = 0;
    const atualizar = (y) => {
      cab.classList.toggle('is-solido', y > 24);
      if (!cab.classList.contains('is-menu')) cab.classList.toggle('is-escondido', y > ultimo + 2 && y > 260);
      if (y < ultimo - 2) cab.classList.remove('is-escondido');
      ultimo = y;
    };
    if (BS.lenis) BS.lenis.on('scroll', ({ scroll }) => atualizar(scroll));
    else window.addEventListener('scroll', () => atualizar(window.scrollY), { passive: true });
    atualizar(window.scrollY);

    // Secções do menu (computador): a seta abre e fecha o painel, para o teclado e
    // o toque; o rato abre-o só de passar. Esc, um clique fora ou sair com o foco
    // fecham-no.
    const grupos = [...cab.querySelectorAll('[data-bs-nav-grupo]')];
    const fecharGrupo = (g) => {
      g.classList.remove('is-aberto');
      g.querySelector('[data-bs-nav-abrir]')?.setAttribute('aria-expanded', 'false');
    };
    grupos.forEach((g) => {
      const seta = g.querySelector('[data-bs-nav-abrir]');
      seta?.addEventListener('click', () => {
        const abrir = !g.classList.contains('is-aberto');
        grupos.forEach(fecharGrupo);
        if (!abrir) return;
        g.classList.add('is-aberto');
        seta.setAttribute('aria-expanded', 'true');
      });
      g.addEventListener('focusout', (e) => { if (!g.contains(e.relatedTarget)) fecharGrupo(g); });
    });
    if (grupos.length) {
      document.addEventListener('click', (e) => grupos.forEach((g) => { if (!g.contains(e.target)) fecharGrupo(g); }));
      document.addEventListener('keydown', (e) => {
        if (e.key !== 'Escape') return;
        const aberto = grupos.find((g) => g.classList.contains('is-aberto'));
        if (!aberto) return;
        fecharGrupo(aberto);
        aberto.querySelector('[data-bs-nav-abrir]')?.focus();
      });
    }

    const botao = cab.querySelector('[data-bs-menu-botao]');
    const menu = document.querySelector('[data-bs-menu]');
    if (!botao || !menu) return;
    const alternar = (abrir) => {
      const aberto = abrir ?? !menu.classList.contains('is-aberto');
      // O menu começa onde o cabeçalho acaba (com a barra de anúncios, se estiver à vista).
      if (aberto) menu.style.setProperty('--bs-menu-topo', `${Math.round(cab.getBoundingClientRect().bottom)}px`);
      menu.classList.toggle('is-aberto', aberto);
      cab.classList.toggle('is-menu', aberto);
      cab.classList.remove('is-escondido');
      botao.setAttribute('aria-expanded', String(aberto));
      menu.setAttribute('aria-hidden', String(!aberto));
      document.documentElement.style.overflow = aberto ? 'hidden' : '';
      if (aberto) BS.lenis?.stop();
      else BS.lenis?.start();
    };
    botao.addEventListener('click', () => alternar());
    menu.addEventListener('click', (e) => { if (e.target.closest('a')) alternar(false); });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && menu.classList.contains('is-aberto')) {
        alternar(false);
        botao.focus();
      }
    });
  }

  // ——— Tema claro e escuro ———
  // O script no <head> já pôs o data-tema antes de pintar; aqui fica a troca: o
  // tema novo abre num círculo a partir do botão (View Transitions) e a escolha
  // fica guardada no browser. Com «sistema», sem escolha guardada, segue o
  // telemóvel ou o computador também depois de abrir.
  function iniciarTema() {
    const html = document.documentElement;
    const cfg = BS.tema || {};
    const meta = document.querySelector('meta[name="theme-color"]');
    const atual = () => (html.dataset.tema === 'claro' ? 'claro' : 'escuro');
    const guardado = () => {
      try {
        return cfg.botao ? localStorage.getItem('bs-tema') : null;
      } catch {
        return null;
      }
    };
    const marcar = () => {
      const t = atual();
      document.querySelectorAll('[data-bs-tema-botao]').forEach((b) => {
        b.setAttribute('aria-label', t === 'claro' ? BS.textos?.temaParaEscuro || '' : BS.textos?.temaParaClaro || '');
      });
      document.querySelectorAll('[data-bs-tema-escolha]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.bsTemaEscolha === t)));
      if (meta && cfg.fundoEscuro) meta.setAttribute('content', t === 'claro' ? cfg.fundoClaro : cfg.fundoEscuro);
    };
    const aplicar = (t, origem) => {
      if (t === atual()) return;
      const mudar = () => {
        html.dataset.tema = t;
        marcar();
        document.dispatchEvent(new CustomEvent('bs:tema', { detail: { tema: t } }));
      };
      if (!document.startViewTransition || BS.reduzido || !origem) {
        mudar();
        return;
      }
      const r = origem.getBoundingClientRect();
      const x = r.left + r.width / 2;
      const y = r.top + r.height / 2;
      const raio = Math.hypot(Math.max(x, window.innerWidth - x), Math.max(y, window.innerHeight - y));
      const transicao = document.startViewTransition(mudar);
      transicao.ready
        .then(() => {
          html.animate(
            { clipPath: [`circle(0px at ${x}px ${y}px)`, `circle(${raio}px at ${x}px ${y}px)`] },
            { duration: 760, easing: 'cubic-bezier(0.65, 0, 0.35, 1)', pseudoElement: '::view-transition-new(root)' },
          );
        })
        .catch(() => {});
    };
    document.addEventListener('click', (e) => {
      const b = e.target.closest('[data-bs-tema-botao], [data-bs-tema-escolha]');
      if (!b) return;
      const t = b.dataset.bsTemaEscolha || (atual() === 'claro' ? 'escuro' : 'claro');
      try {
        localStorage.setItem('bs-tema', t);
      } catch {}
      aplicar(t, b);
    });
    if (cfg.padrao === 'sistema' && window.matchMedia) {
      const mq = window.matchMedia('(prefers-color-scheme: light)');
      mq.addEventListener?.('change', () => {
        if (!guardado()) aplicar(mq.matches ? 'claro' : 'escuro');
      });
    }
    marcar();
  }

  // ——— Revelações ———
  let observador;
  function iniciarRevelacoes(raiz = document) {
    const els = raiz.querySelectorAll('[data-bs-revelar]:not(.is-visivel)');
    if (BS.reduzido || !('IntersectionObserver' in window)) {
      els.forEach((el) => el.classList.add('is-visivel'));
      return;
    }
    observador ||= new IntersectionObserver((entradas) => {
      entradas.forEach((en) => {
        if (!en.isIntersecting) return;
        en.target.classList.add('is-visivel');
        observador.unobserve(en.target);
      });
    }, { rootMargin: '0px 0px -6% 0px', threshold: 0.06 });
    els.forEach((el) => observador.observe(el));
  }

  // O SplitText põe aria-label no elemento que divide; num <span> isso não vale.
  // O nome passa para o título à volta (o texto todo) e os pedaços ficam escondidos.
  BS.ariaDividir = (el) => {
    if (!el.matches('span')) return 'auto';
    const titulo = el.closest('h1, h2, h3, h4, h5, h6');
    if (!titulo) return 'none';
    if (!titulo.hasAttribute('aria-label')) titulo.setAttribute('aria-label', titulo.textContent.replace(/\s+/g, ' ').trim());
    return 'hidden';
  };

  // Títulos que sobem linha a linha (máscara).
  function iniciarTitulos(raiz = document) {
    if (!window.gsap || !window.SplitText || BS.reduzido) return;
    raiz.querySelectorAll('[data-bs-dividir]').forEach((el) => {
      if (el.dataset.dividido) return;
      el.dataset.dividido = '1';
      document.fonts.ready.then(() => {
        const split = SplitText.create(el, { type: 'lines', mask: 'lines', linesClass: 'bs-linha-split', aria: BS.ariaDividir(el) });
        gsap.from(split.lines, {
          yPercent: 115,
          rotate: 2,
          duration: 1.15,
          ease: 'expo.out',
          stagger: 0.09,
          scrollTrigger: { trigger: el, start: 'top 90%', once: true },
          onComplete: () => split.revert(),
        });
      });
    });
  }

  // ——— Cartões com inclinação 3D ———
  function iniciarInclinacao(raiz = document) {
    if (BS.toque || BS.reduzido) return;
    raiz.querySelectorAll('[data-bs-inclinar]').forEach((el) => {
      if (el.dataset.inclinado) return;
      el.dataset.inclinado = '1';
      const alvo = el.querySelector('[data-bs-inclinar-alvo]') || el;
      const forca = Number(el.dataset.bsInclinar || 10);
      el.addEventListener('pointermove', (e) => {
        const r = alvo.getBoundingClientRect();
        const x = (e.clientX - r.left) / r.width;
        const y = (e.clientY - r.top) / r.height;
        el.style.setProperty('--ry', `${(x - 0.5) * forca}deg`);
        el.style.setProperty('--rx', `${(0.5 - y) * forca * 0.8}deg`);
        el.style.setProperty('--gx', `${x * 100}%`);
        el.style.setProperty('--gy', `${y * 100}%`);
      });
      el.addEventListener('pointerleave', () => {
        el.style.setProperty('--rx', '0deg');
        el.style.setProperty('--ry', '0deg');
      });
    });
  }

  // ——— Botões magnéticos ———
  function iniciarMagneticos(raiz = document) {
    if (BS.toque || BS.reduzido || !window.gsap) return;
    raiz.querySelectorAll('[data-bs-magnetico]').forEach((el) => {
      if (el.dataset.magnetico) return;
      el.dataset.magnetico = '1';
      const xTo = gsap.quickTo(el, 'x', { duration: 0.6, ease: 'elastic.out(1, 0.4)' });
      const yTo = gsap.quickTo(el, 'y', { duration: 0.6, ease: 'elastic.out(1, 0.4)' });
      el.addEventListener('pointermove', (e) => {
        const r = el.getBoundingClientRect();
        xTo((e.clientX - (r.left + r.width / 2)) * 0.25);
        yTo((e.clientY - (r.top + r.height / 2)) * 0.35);
      });
      el.addEventListener('pointerleave', () => { xTo(0); yTo(0); });
    });
  }

  // ——— Paralaxe simples: data-bs-paralaxe="0.15" ———
  function iniciarParalaxe(raiz = document) {
    if (!window.gsap || !window.ScrollTrigger || BS.reduzido) return;
    raiz.querySelectorAll('[data-bs-paralaxe]').forEach((el) => {
      if (el.dataset.paralaxeAtivo) return;
      el.dataset.paralaxeAtivo = '1';
      const k = Number(el.dataset.bsParalaxe) || 0.15;
      gsap.fromTo(el, { yPercent: k * 100 }, {
        yPercent: -k * 100,
        ease: 'none',
        scrollTrigger: { trigger: el, start: 'top bottom', end: 'bottom top', scrub: true },
      });
    });
  }

  // ——— Faixas em movimento (aceleram com o scroll) ———
  function iniciarFaixas(raiz = document) {
    raiz.querySelectorAll('[data-bs-marquee]').forEach((el) => {
      if (el.dataset.ativo) return;
      el.dataset.ativo = '1';
      const trilho = el.querySelector('[data-bs-trilho]');
      if (!trilho || BS.reduzido || !window.gsap) return;
      const dir = el.dataset.direcao === 'direita' ? 1 : -1;
      const base = Number(el.dataset.velocidade || 70);
      let x = 0;
      let extra = 0;
      let largura = trilho.scrollWidth / 2;
      new ResizeObserver(() => { largura = trilho.scrollWidth / 2; }).observe(trilho);
      let visivel = true;
      new IntersectionObserver(([en]) => { visivel = en.isIntersecting; }).observe(el);
      const passo = (t, dt) => {
        if (!el.isConnected) return gsap.ticker.remove(passo);
        if (!visivel) return;
        extra += (Math.min(Math.abs(BS.velocidade) * 0.35, 1400) - extra) * 0.07;
        x += dir * (base + extra) * (dt / 1000);
        if (x <= -largura) x += largura;
        if (x > 0) x -= largura;
        trilho.style.transform = `translate3d(${x}px,0,0)`;
      };
      gsap.ticker.add(passo);
    });
  }

  // ——— Carrinho ———
  const Carrinho = {
    get el() { return document.querySelector('[data-bs-gaveta]'); },

    abrir() {
      const g = this.el;
      if (!g) return (window.location.href = BS.rotas.carrinho);
      g.classList.add('is-aberta');
      g.setAttribute('aria-hidden', 'false');
      document.documentElement.style.overflow = 'hidden';
      BS.lenis?.stop();
      setTimeout(() => g.querySelector('[data-bs-fechar-gaveta]:not(.bs-gaveta__fundo)')?.focus(), 350);
    },

    fechar() {
      const g = this.el;
      if (!g) return;
      g.classList.remove('is-aberta');
      g.setAttribute('aria-hidden', 'true');
      document.documentElement.style.overflow = '';
      BS.lenis?.start();
    },

    // Substitui o conteúdo da gaveta pelo HTML que o Shopify devolve (Section Rendering API).
    pintar(secoes) {
      const html = secoes?.['bs-carrinho-gaveta'];
      if (!html) return;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const novo = doc.querySelector('#bs-gaveta-conteudo');
      const atual = document.querySelector('#bs-gaveta-conteudo');
      if (novo && atual) atual.replaceWith(novo);
      this.contar(Number(novo?.dataset.contagem || 0));
    },

    contar(n) {
      document.querySelectorAll('[data-bs-contador]').forEach((c) => {
        c.textContent = n > 0 ? String(n) : '';
        c.dataset.n = String(n);
        c.classList.remove('is-pulo');
        void c.offsetWidth;
        if (n > 0) c.classList.add('is-pulo');
      });
    },

    async adicionar(formData, { abrir = true } = {}) {
      formData.append('sections', 'bs-carrinho-gaveta');
      formData.append('sections_url', window.location.pathname);
      const r = await fetch(`${BS.rotas.adicionar}.js`, {
        method: 'POST',
        headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' },
        body: formData,
      });
      const dados = await r.json();
      if (!r.ok || dados.status) throw new Error(dados.description || dados.message || BS.textos.erro);
      this.pintar(dados.sections);
      document.dispatchEvent(new CustomEvent('bs:adicionado', { detail: dados }));
      if (abrir) this.abrir();
      return dados;
    },

    async alterar(linha, quantidade) {
      const gaveta = document.querySelector('#bs-gaveta-conteudo');
      gaveta?.classList.add('is-ocupada');
      try {
        const r = await fetch(`${BS.rotas.alterar}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ line: linha, quantity: quantidade, sections: 'bs-carrinho-gaveta', sections_url: window.location.pathname }),
        });
        const dados = await r.json();
        if (!r.ok || dados.status) throw new Error(dados.description || dados.message || BS.textos.erro);
        this.pintar(dados.sections);
        document.dispatchEvent(new CustomEvent('bs:carrinho-alterado', { detail: dados }));
      } catch (err) {
        const aviso = document.querySelector('[data-bs-gaveta-erro]');
        if (aviso) {
          aviso.hidden = false;
          aviso.textContent = err.message;
        }
      } finally {
        document.querySelector('#bs-gaveta-conteudo')?.classList.remove('is-ocupada');
      }
    },
  };
  BS.carrinho = Carrinho;

  // Anti-robôs (Turnstile da Cloudflare) para o chat e os orçamentos: só carrega
  // quando é preciso e só se houver chave nas Definições do tema. Cada token
  // serve uma vez; devolve '' sem chave (a app aceita quando também não tem a secreta).
  BS.turnstile = async (alvo) => {
    const chave = BS.app?.turnstile;
    if (!chave) return '';
    if (!window.turnstile) {
      await new Promise((ok, falha) => {
        const sc = document.createElement('script');
        sc.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
        sc.async = true;
        sc.onload = ok;
        sc.onerror = () => falha(new Error(BS.textos.erro));
        document.head.appendChild(sc);
      });
    }
    const caixa = document.createElement('div');
    alvo.appendChild(caixa);
    try {
      return await new Promise((ok, falha) => {
        window.turnstile.render(caixa, {
          sitekey: chave, appearance: 'interaction-only', language: 'pt',
          theme: document.documentElement.dataset.tema === 'claro' ? 'light' : 'dark',
          callback: ok, 'error-callback': () => falha(new Error(BS.textos.erro)),
        });
      });
    } finally {
      setTimeout(() => caixa.remove(), 0);
    }
  };

  // NIF para a fatura: 9 dígitos, o primeiro certo e o dígito de controlo
  // (igual à app). Vazio também serve: é opcional.
  BS.nifValido = (nif) => {
    const n = String(nif || '').replace(/\D/g, '');
    if (!/^\d{9}$/.test(n) || !/^([1235689]|45|7[0-9])/.test(n)) return false;
    const soma = [...n.slice(0, 8)].reduce((s, d, i) => s + Number(d) * (9 - i), 0);
    const controlo = 11 - (soma % 11);
    return (controlo >= 10 ? 0 : controlo) === Number(n[8]);
  };
  const limparNif = (v) => String(v || '').toUpperCase().replace(/^PT/, '').replace(/[\s.-]/g, '');

  function verificarNif(input) {
    const caixa = input.closest('[data-bs-nif]');
    const v = limparNif(input.value);
    const ok = !v || BS.nifValido(v);
    caixa?.classList.toggle('is-erro', !ok);
    const erro = caixa?.querySelector('[data-bs-nif-erro]');
    if (erro) erro.hidden = ok;
    input.setAttribute('aria-invalid', String(!ok));
    return ok ? v : null;
  }

  async function guardarNif(input) {
    const v = verificarNif(input);
    if (v === null || v === (input.dataset.guardado ?? input.defaultValue)) return;
    input.value = v;
    input.dataset.guardado = v;
    // Os dois campos (gaveta e página) ficam iguais.
    document.querySelectorAll('[data-bs-nif-input]').forEach((o) => { if (o !== input) o.value = v; });
    try {
      await fetch(`${BS.rotas.atualizar}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes: { NIF: v } }),
      });
    } catch {
      // Se falhar, o NIF segue na mesma com o formulário do checkout.
    }
  }

  function iniciarCarrinho() {
    document.addEventListener('click', (e) => {
      const abrir = e.target.closest('[data-bs-abrir-gaveta]');
      if (abrir && Carrinho.el) {
        e.preventDefault();
        Carrinho.abrir();
        return;
      }
      if (e.target.closest('[data-bs-fechar-gaveta]')) {
        e.preventDefault();
        Carrinho.fechar();
        return;
      }
      const botao = e.target.closest('[data-bs-linha-alterar]');
      if (botao) {
        e.preventDefault();
        const linha = Number(botao.dataset.linha);
        const q = Number(botao.dataset.quantidade);
        Carrinho.alterar(linha, Math.max(0, q));
      }
    });
    document.addEventListener('change', (e) => {
      const input = e.target.closest('[data-bs-linha-input]');
      if (input) Carrinho.alterar(Number(input.dataset.linha), Math.max(0, Number(input.value)));
      const nif = e.target.closest('[data-bs-nif-input]');
      if (nif) guardarNif(nif);
    });
    document.addEventListener('input', (e) => {
      const nif = e.target.closest('[data-bs-nif-input]');
      if (nif && nif.closest('[data-bs-nif]')?.classList.contains('is-erro')) verificarNif(nif);
    });
    // Não se vai para o checkout com um NIF errado.
    document.addEventListener('submit', (e) => {
      const nif = e.target.querySelector?.('[data-bs-nif-input]');
      if (!nif || e.submitter?.name !== 'checkout') return;
      const v = verificarNif(nif);
      if (v === null) {
        e.preventDefault();
        nif.focus();
      } else nif.value = v;
    }, true);
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && Carrinho.el?.classList.contains('is-aberta')) Carrinho.fechar();
    });

    // Formulários de produto simples (sem personalização) em qualquer página.
    document.addEventListener('submit', async (e) => {
      const form = e.target.closest('form[data-bs-form-produto]');
      if (!form || form.dataset.bsProprio !== undefined) return;
      e.preventDefault();
      const botao = form.querySelector('[type="submit"]');
      botao?.classList.add('is-carregando');
      try {
        await Carrinho.adicionar(new FormData(form));
      } catch (err) {
        const aviso = form.querySelector('[data-bs-erro]');
        if (aviso) {
          aviso.hidden = false;
          aviso.textContent = err.message;
        }
      } finally {
        botao?.classList.remove('is-carregando');
      }
    });
  }

  // ——— «Falar comigo» (snippets/bs-contacto.liquid) ———
  // Abre e fecha os contactos; fecha com Esc, com um clique fora ou ao escolher um.
  function iniciarContacto() {
    const raiz = document.querySelector('[data-bs-contacto]');
    if (!raiz) return;
    const botao = raiz.querySelector('[data-bs-contacto-botao]');
    raiz.querySelectorAll('.bs-contacto__lista li').forEach((li, i) => li.style.setProperty('--i', i));
    const alternar = (abrir, { foco = false } = {}) => {
      const aberto = abrir ?? botao.getAttribute('aria-expanded') !== 'true';
      botao.setAttribute('aria-expanded', String(aberto));
      raiz.classList.toggle('is-aberto', aberto);
      if (aberto && foco) setTimeout(() => raiz.querySelector('.bs-contacto__lista a')?.focus({ preventScroll: true }), 80);
    };
    botao.addEventListener('click', (e) => alternar(undefined, { foco: e.detail === 0 })); // pelo teclado, o foco vai para a primeira opção
    raiz.addEventListener('click', (e) => { if (e.target.closest('.bs-contacto__lista a')) alternar(false); });
    document.addEventListener('click', (e) => { if (raiz.classList.contains('is-aberto') && !raiz.contains(e.target)) alternar(false); });
    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape' || !raiz.classList.contains('is-aberto')) return;
      alternar(false);
      botao.focus();
    });
    // A barra de compra do produto (telemóvel): o botão sobe a altura dela.
    const barra = document.querySelector('.bs-produto__barra');
    if (barra) {
      const medir = () => raiz.style.setProperty('--bs-contacto-barra', `${Math.round(barra.getBoundingClientRect().height + 10)}px`);
      medir();
      window.addEventListener('resize', medir, { passive: true });
    }
  }

  // ——— Arranque ———
  BS.iniciar = (raiz = document) => {
    iniciarRevelacoes(raiz);
    iniciarTitulos(raiz);
    iniciarInclinacao(raiz);
    iniciarMagneticos(raiz);
    iniciarFaixas(raiz);
    iniciarParalaxe(raiz);
  };

  iniciarScroll();
  iniciarCabecalho();
  iniciarTema();
  iniciarCarrinho();
  iniciarContacto();
  BS.iniciar();

  // Recalcular posições quando as fontes e as imagens acabam de carregar.
  const refrescar = () => window.ScrollTrigger && ScrollTrigger.refresh();
  document.fonts?.ready.then(refrescar);
  window.addEventListener('load', refrescar);

  // Editor de temas: reiniciar o que foi redesenhado.
  document.addEventListener('shopify:section:load', (e) => {
    BS.iniciar(e.target);
    refrescar();
  });
})();
